/**
 * FitResultCard.js
 *
 * RC 1.44.H5 — cover letter REMOVED from scoring. Pre-H5 cards
 * rendered a "Letter fit" chip + "no letter" caveat footer; both are
 * gone. Every analysis is CV-only by construction now.
 *
 * Shows:
 *   - Overall Fit (primary large number)
 *   - NDOR CV verdict (sole sub-fit chip)
 *   - Final decision label
 *   - "Why this score" diagnostic block
 *   - Reasoning summary
 *   - Sub-scores breakdown (Salary nullable per RC 1.44.H6)
 *   - Key strengths and risks
 *
 * Reads from `reasoning_json`:
 *   cv_sub_score, overall_fit_score, score_explanation, gap_count,
 *   is_transition_role, bounds_applied. All optional — degrades cleanly.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp, TrendingDown, Target, Shield, MapPin,
  DollarSign, Briefcase, Brain, FileText, Sparkles,
} from 'lucide-react';
import { cleanPhrase } from '@/utils/interviewIntelligence';
import { sanitiseNarrative } from '@/utils/narrativeSanitiser';

/** Human-friendly labels for the job_decision enum. */
const DECISION_LABELS = {
  reject: { label: 'Reject', className: 'bg-ndor-error-light text-ndor-error border-ndor-error/20' },
  hold: { label: 'Hold', className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20' },
  shortlist: { label: 'Shortlist', className: 'bg-ndor-success-light text-ndor-success border-ndor-success/20' },
  apply_with_repositioning: { label: 'Tailor application', className: 'bg-ndor-info-light text-ndor-info border-ndor-info/20' },
};

const BOUND_COPY = {
  gaps_ge_3:           (cap) => `Three or more significant gaps capped the overall fit at ${cap}.`,
  transition_role:     (cap) => `Significant role transition capped the overall fit at ${cap}.`,
  // RC 1.44.H5 — `weak_cv_letter_limit` retired alongside cover-
  // letter scoring. Legacy rows in the DB may still carry it; if
  // surfaced, the fallback in the rendering switch ("Capped at X")
  // covers them.
};

// RC 1.24.b.1 — Transparency lines. Surfaced when NDOR identified a
// major limiting factor but no formal cap actually fired. RC 1.44.H5
// dropped the letter-related lines.
function buildTransparencyLines({ gapCount, isTransition, boundsApplied }) {
  const lines = [];
  const firedRules = new Set((boundsApplied || []).map((b) => b.rule));
  if (gapCount >= 3 && !firedRules.has('gaps_ge_3')) {
    lines.push('Three or more significant gaps limited the possible score range.');
  }
  if (isTransition && !firedRules.has('transition_role')) {
    lines.push('A significant role transition constrained the achievable ceiling.');
  }
  return lines;
}

/** Colour-code a 0-100 score. */
function scoreColor(score) {
  if (score >= 70) return 'text-ndor-success';
  if (score >= 45) return 'text-ndor-warning';
  return 'text-ndor-error';
}

function subChipTone(score) {
  if (score == null) return 'text-ndor-ink-muted border-ndor-edge bg-ndor-surface-alt/50';
  if (score >= 70)   return 'text-ndor-success border-ndor-success/30 bg-ndor-success-light/40';
  if (score >= 45)   return 'text-ndor-warning border-ndor-warning/30 bg-ndor-warning-light/40';
  return 'text-ndor-error border-ndor-error/30 bg-ndor-error-light/40';
}

/** Small score bar with label. */
// RC 1.48.5A — one-liner glossary for the five rubric sub-scores.
// Users have asked what each label means (notably "Risk", which
// reads as ambiguous when shown alone as a red bar). Surfacing the
// meaning via a `title` tooltip + a visible legend block keeps the
// numeric layout intact while making the semantics explicit.
const SUBSCORE_MEANING = {
  Salary:    'Salary alignment: how close the role pay band is to the candidate expectation.',
  Seniority: 'Seniority fit: how closely the candidate level matches the role level.',
  Location:  'Location alignment: geographic / remote compatibility.',
  Strategic: 'Strategic fit: how central the candidate profile is to the role narrative.',
  Risk:      'Risk: evidence gaps, red flags and application-risk factors. Lower = safer application.',
};

function ScoreRow({ icon: Icon, label, score }) {
  const hint = SUBSCORE_MEANING[label] || '';
  return (
    <div className="flex items-center gap-3" title={hint}>
      <Icon className="h-4 w-4 text-ndor-ink-muted shrink-0" />
      <span className="text-sm text-ndor-ink-secondary w-28 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-ndor-surface-alt rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${score >= 70 ? 'bg-ndor-success' : score >= 45 ? 'bg-ndor-warning' : 'bg-ndor-error'}`}
          style={{ width: `${Math.min(score, 100)}%` }}
        />
      </div>
      <span className={`text-sm font-medium w-8 text-right ${scoreColor(score)}`}>{score}</span>
    </div>
  );
}

function SubScoreChip({ icon: Icon, label, score, testid }) {
  const tone = subChipTone(score);
  return (
    <div
      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${tone}`}
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[10px] font-mono-ui uppercase tracking-[0.14em]">{label}</span>
      <span className="text-sm font-semibold tabular-nums">
        {score == null ? '—' : score}
      </span>
    </div>
  );
}

export default function FitResultCard({ score, scope = 'pack' }) {
  if (!score) return null;
  // RC 1.24Z — `scope` tells the card whether it is rendering a
  // pack-aware analysis (default: inside the Application Pack
  // detail) or a job-level analysis (e.g. the Job Detail page).
  // RC 1.44.H5 — both scopes now render the SAME single CV-only
  // chip set; the historical letter-related differences are gone.
  const isJobScope = scope === 'job';

  // job_scores sub-score columns are numeric(5,4) in the DB (0.0–0.9999).
  // Normalize to 0–100 integers here for display. Accepts both scaled
  // (≤1) and raw (>1) values to stay safe.
  const toInt = (v) => {
    if (v == null) return null;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return null;
    return Math.round(n > 1 ? n : n * 100);
  };
  const reasoning = score.reasoning_json || {};

  // RC 1.24.b — prefer the explicit overall number from reasoning_json
  // if present; fall back to the legacy top-level fit_score.
  const overall =
    toInt(reasoning.overall_fit_score) ??
    toInt(score.fit_score) ??
    0;
  const cvSub = toInt(reasoning.cv_sub_score);
  // RC 1.47.VARIANT-CV-VERDICT-PARITY Layer 1 (2026-05-03) —
  // when this row was scored against an un-finalized cv_variant
  // (pre-finalize "preview" Path B run), surface the operator-
  // locked annotation "Preview CV verdict — finalize to confirm"
  // adjacent to the verdict chip. Legacy rows lack the key →
  // coerces to false. Finalized variants adopt
  // `cv_variants.cv_sub_score_at_finalize` and skip Path B
  // entirely (backend §1850-1869 in routes/jobs.py), so this
  // flag is false for the canonical case.
  const cvSubProvisional = reasoning.cv_sub_score_provisional === true;
  // RC 1.44.H6 — salary nullable (unlike other sub-scores which
  // remain mandatory 0–100).
  // RC 1.46.NARRATIVE-INTEGRITY (2026-05-02) — sub-score nulls are
  // preserved end-to-end so the breakdown can hide unassessed rows
  // (the LLM emitted `null` because the JD/profile didn't expose a
  // signal). Genuine 0 scores stay visible. Previously `?? 0`
  // collapsed the two cases.
  const s = {
    salary_score:    toInt(score.salary_score),
    seniority_score: toInt(score.seniority_score),
    location_score:  toInt(score.location_score),
    strategic_score: toInt(score.strategic_score),
    risk_score:      toInt(score.risk_score),
    final_decision:  score.final_decision,
  };

  const decision = DECISION_LABELS[s.final_decision] || DECISION_LABELS.hold;
  const strengths = (reasoning.key_strengths || []).map(cleanPhrase).filter(Boolean);
  const risks = (reasoning.key_risks || []).map(cleanPhrase).filter(Boolean);
  const summary = reasoning.reasoning_summary || '';
  // RC 1.46.NARRATIVE-INTEGRITY (2026-05-02) — strip "/100",
  // percentages, and numeric sub-score references from the
  // narrative. The numbers shown by Path A's holistic self-grade
  // contradict the strict Path B chip the UI displays. The
  // backend prompt already enforces qualitative phrasing on new
  // analyses; this client-side sanitiser shields the UI from
  // legacy `score_explanation` rows that still carry numbers.
  const explanation = sanitiseNarrative(reasoning.score_explanation || '').trim();
  const bounds = Array.isArray(reasoning.bounds_applied) ? reasoning.bounds_applied : [];
  // RC 1.44.H4 — surface auto-refund badge when the post-LLM
  // governance check found this fresh variant under-performed the
  // Source CV baseline and re-credited the wallet.
  const refunded = reasoning.refunded === true;
  const transparencyLines = buildTransparencyLines({
    gapCount: typeof reasoning.gap_count === 'number' ? reasoning.gap_count : 0,
    isTransition: reasoning.is_transition_role === true,
    boundsApplied: bounds,
  });
  const showExplanationBlock =
    !!explanation || bounds.length > 0 || transparencyLines.length > 0 || refunded;

  return (
    <Card className="border-ndor-edge shadow-sm" data-testid="fit-result-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium text-ndor-ink flex items-center gap-2">
            <Target className="h-5 w-5 text-ndor-ink-muted" />
            {isJobScope ? 'Job-level fit' : 'Fit Analysis'}
          </CardTitle>
          <Badge variant="outline" className={`${decision.className} font-medium`} data-testid="fit-decision-badge">
            {decision.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall + CV verdict — no Letter chip post-H5 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div
              className={`text-4xl font-bold font-outfit ${scoreColor(overall)}`}
              data-testid="fit-score-value"
            >
              {overall}
            </div>
            <div>
              <p className="text-xs text-ndor-ink-muted uppercase tracking-wide">Overall Fit</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:ml-auto items-center">
            <SubScoreChip icon={FileText} label="NDOR CV verdict" score={cvSub} testid="fit-sub-cv" />
            {cvSubProvisional && cvSub != null && (
              <span
                className="text-[11px] font-medium text-amber-200 dark:text-amber-300"
                data-testid="cv-verdict-provisional-label"
              >
                Preview CV verdict — finalize to confirm
              </span>
            )}
          </div>
        </div>

        {/* RC 1.42 P0.5 — "Why this score" — promoted diagnostic
            block. Renamed from "What moved the score" so the
            framing is explicitly diagnostic ("here's why you scored
            X, including which sub-scores held you back"). */}
        {showExplanationBlock && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/50 px-3 py-2.5"
            data-testid="fit-score-explanation"
          >
            <p className="text-[10px] font-mono-ui uppercase tracking-[0.18em] text-ndor-gold flex items-center gap-1.5 mb-1">
              <Sparkles className="h-3 w-3" /> Why this score
            </p>
            {explanation && (
              <p className="text-sm text-ndor-ink leading-relaxed font-manrope">
                {explanation}
              </p>
            )}
            {/* Surfaces the lowest-scoring sub-score so the candidate
                immediately sees where they lost the most points.
                Computed from the same `s` dict that drives the
                breakdown bars below — no extra LLM call.
                RC 1.46.NARRATIVE-INTEGRITY (2026-05-02) — qualitative
                only. The numeric sub-score is shown in the breakdown
                rows below; reproducing it here would (a) duplicate
                a canonical number into prose-adjacent territory and
                (b) reintroduce the `/100` framing the sanitiser
                strips from the explanation sentence directly above. */}
            {(() => {
              const subs = [
                { label: 'Salary',    v: s.salary_score },
                { label: 'Seniority', v: s.seniority_score },
                { label: 'Location',  v: s.location_score },
                { label: 'Strategic', v: s.strategic_score },
                { label: 'Risk',      v: s.risk_score },
              ].filter(x => typeof x.v === 'number');
              if (subs.length === 0) return null;
              const lowest = subs.reduce((a, b) => (a.v <= b.v ? a : b));
              if (lowest.v >= 80) return null; // no diagnostic value
              return (
                <p
                  className="mt-1.5 text-xs text-ndor-ink-secondary font-manrope"
                  data-testid="fit-score-largest-gap"
                >
                  • Largest gap: <span className="font-medium">{lowest.label}</span> — lowest assessed area.
                </p>
              );
            })()}
            {bounds.length > 0 && (
              <ul className="mt-1.5 space-y-0.5" data-testid="fit-score-bounds">
                {bounds.map((b, i) => (
                  <li key={i} className="text-xs text-ndor-ink-secondary font-manrope">
                    • {BOUND_COPY[b.rule] ? BOUND_COPY[b.rule](b.cap) : `Capped at ${b.cap} (${b.rule}).`}
                  </li>
                ))}
              </ul>
            )}
            {transparencyLines.length > 0 && (
              <ul
                className="mt-1.5 space-y-0.5"
                data-testid="fit-score-transparency"
              >
                {transparencyLines.map((t, i) => (
                  <li key={i} className="text-xs text-ndor-ink-muted font-manrope italic">
                    · {t}
                  </li>
                ))}
              </ul>
            )}
            {refunded && (
              <p
                className="mt-1.5 text-[11px] text-ndor-success font-manrope"
                data-testid="fit-refund-notice"
              >
                Credit refunded — this variant scored below your Source CV baseline.
              </p>
            )}
          </div>
        )}

        {/* Reasoning summary */}
        {summary && (
          <p className="text-sm text-ndor-ink-secondary leading-relaxed font-manrope" data-testid="fit-reasoning-summary">
            {summary}
          </p>
        )}

        {/* Sub-scores breakdown */}
        <div className="space-y-2.5">
          <p className="text-xs text-ndor-ink-muted uppercase tracking-wide mb-1">Score Breakdown</p>
          {/* RC 1.46.NARRATIVE-INTEGRITY (2026-05-02) — null sub-score
              rows are hidden generically. The LLM emits `null` when
              the JD/profile expose no signal for a dimension (per
              `_CV_SAFETY_RULES`/RC 1.44.H6). Rendering "Salary —"
              implied "salary was considered" when in fact the
              dimension was unassessable. The same shield now applies
              to seniority/location/strategic/risk. Genuine 0 scores
              remain visible. */}
          {[
            { key: 'salary',    icon: DollarSign, label: 'Salary',    v: s.salary_score },
            { key: 'seniority', icon: Briefcase,  label: 'Seniority', v: s.seniority_score },
            { key: 'location',  icon: MapPin,     label: 'Location',  v: s.location_score },
            { key: 'strategic', icon: Brain,      label: 'Strategic', v: s.strategic_score },
            { key: 'risk',      icon: Shield,     label: 'Risk',      v: s.risk_score },
          ]
            .filter((row) => row.v != null)
            .map((row) => (
              <ScoreRow key={row.key} icon={row.icon} label={row.label} score={row.v} />
            ))}
        </div>

        {/* RC 1.46.NARRATIVE-INTEGRITY (2026-05-02) — qualitative
            low-score callout. Bottom-quartile sub-scores (<45) are
            surfaced as plain-language risks so users still see
            "where the CV is weakest" even though the explanation
            block now omits numbers. Numeric values remain on the
            bars above; this section is prose only. */}
        {(() => {
          const subs = [
            { label: 'Salary',    v: s.salary_score },
            { label: 'Seniority', v: s.seniority_score },
            { label: 'Location',  v: s.location_score },
            { label: 'Strategic', v: s.strategic_score },
            { label: 'Risk',      v: s.risk_score },
          ].filter((x) => typeof x.v === 'number' && x.v < 45);
          if (subs.length === 0) return null;
          return (
            <div
              className="rounded-md border border-ndor-warning/30 bg-ndor-warning-light/30 px-3 py-2.5"
              data-testid="fit-low-score-callout"
            >
              <p className="text-[10px] font-mono-ui uppercase tracking-[0.18em] text-ndor-warning mb-1">
                Areas of weakness
              </p>
              <ul className="space-y-0.5">
                {subs.map((x) => {
                  // RC 1.48.5A — when the only low sub-score is
                  // Risk, spell out what that means so users don't
                  // read a red "14" as "the CV is bad." Risk is a
                  // distinct evidence-gap / red-flag signal.
                  const isRisk = x.label === 'Risk';
                  return (
                  <li
                    key={x.label}
                    className="text-xs text-ndor-ink-secondary font-manrope"
                    data-testid={`fit-low-score-${x.label.toLowerCase()}`}
                  >
                    • <span className="font-medium">{x.label}</span>{' '}
                    {isRisk
                      ? (
                        <>
                          signals evidence gaps or application risk
                          factors (e.g. location not stated, short tenure
                          streak, missing nice-to-haves).{' '}
                          <span className="text-ndor-ink-muted">
                            A low Risk score does <em>not</em> lower your overall fit
                            — it flags things to pre-empt in the cover letter or
                            interview.
                          </span>
                        </>
                      )
                      : <>shows limited alignment with this role.</>}
                  </li>
                );})}
              </ul>
            </div>
          );
        })()}

        {/* Strengths and risks */}
        {(strengths.length > 0 || risks.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-ndor-edge">
            {strengths.length > 0 && (
              <div>
                <p className="text-xs text-ndor-ink-muted uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-ndor-success" /> Strengths
                </p>
                <ul className="space-y-1">
                  {strengths.map((s, i) => (
                    <li key={i} className="text-sm text-ndor-ink-secondary font-manrope">• {s}</li>
                  ))}
                </ul>
              </div>
            )}
            {risks.length > 0 && (
              <div>
                <p className="text-xs text-ndor-ink-muted uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <TrendingDown className="h-3.5 w-3.5 text-ndor-error" /> Risks
                </p>
                <ul className="space-y-1">
                  {risks.map((r, i) => (
                    <li key={i} className="text-sm text-ndor-ink-secondary font-manrope">• {r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
